// Экран загрузки: логотип игры с дымом, полоса прогресса, проценты и логотип студии.
//
// Полоса собрана из кадров листа preloader: подложка, заливка, маска формы и рамка сверху.
// Заливка прямоугольная и растёт слева направо, скруглённые концы ей отрезает маска.

import { Container, Sprite, Text, type Rectangle, type TextStyleOptions } from 'pixi.js';

import type { PreloaderFrame } from '../assets/catalog';
import type { Spine, TextureSheet } from '../assets/manager';
import { showLogo, type Scene } from '../ui/scene';

// Координаты внутри макета отсчитываются от его центра.
const LOGO_Y: number = -210;
const BAR_Y: number = 190;
const PERCENT_Y: number = 280;
const STUDIO_LOGO_Y: number = 430;

/** Логотип нарисован крупнее, чем нужен экрану загрузки, но дым надо оставить заметным. */
const LOGO_SCALE: number = 0.75;

/** Логотип студии стоит внизу и в глаза не лезет. */
const STUDIO_LOGO_SCALE: number = 0.5;

const PERCENT_STYLE: TextStyleOptions = {
  fontFamily: 'system-ui, sans-serif',
  fontSize: 40,
  fill: 0xffffff,
};

/** Экран загрузки с полосой прогресса. */
export class LoadingScene implements Scene {
  readonly view: Container = new Container();

  readonly #fill: Sprite;
  readonly #percent: Text;
  readonly #barWidth: number;

  constructor(sheet: TextureSheet<PreloaderFrame>, logo: Spine) {
    showLogo(logo, LOGO_SCALE, LOGO_Y);

    const studioLogo = new Sprite(sheet.studioLogo);
    studioLogo.anchor.set(0.5);
    studioLogo.y = STUDIO_LOGO_Y;
    studioLogo.scale.set(STUDIO_LOGO_SCALE);

    const frame = new Sprite(sheet.progressFrame);
    frame.anchor.set(0.5);

    const back = new Sprite(sheet.progressBack);
    back.anchor.set(0.5);

    // Подложка обрезана по краям листа, поэтому её trim — это внутренность полосы.
    // Заливка идёт по ней, а не по всей ширине рамки: иначе крайние проценты прячутся под рамкой.
    // У необрезанного кадра trim не задан, тогда внутренность — весь кадр.
    const interior: Rectangle = back.texture.trim ?? back.texture.orig;
    this.#barWidth = interior.width;

    // Заливка растёт от левого края внутренности, поэтому её якорь стоит на нём.
    this.#fill = new Sprite(sheet.progressFill);
    this.#fill.anchor.set(0, 0.5);
    this.#fill.x = interior.x - back.texture.orig.width / 2;
    this.#fill.height = frame.height;
    this.#fill.width = 0;

    const mask = new Sprite(sheet.progressMask);
    mask.anchor.set(0.5);

    // Маска обрезает только заливку, поэтому она лежит в своём контейнере.
    const filled = new Container();
    filled.addChild(this.#fill, mask);
    filled.mask = mask;

    this.#percent = new Text({ text: '0%', style: PERCENT_STYLE });
    this.#percent.anchor.set(0.5);
    this.#percent.y = PERCENT_Y;

    const bar = new Container();
    bar.y = BAR_Y;
    bar.addChild(back, filled, frame);

    this.view.addChild(logo, bar, this.#percent, studioLogo);
  }

  /** Показывает долю загрузки от 0 до 1. */
  setProgress(progress: number): void {
    const value: number = Math.min(Math.max(progress, 0), 1);

    this.#fill.width = this.#barWidth * value;
    this.#percent.text = `${Math.round(value * 100)}%`;
  }

  layout(): void {
    // Содержимое стоит в центре макета, поэтому от полей по краям оно не зависит.
  }

  destroy(): void {
    this.view.destroy({ children: true });
  }
}
